import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Loader2, Wallet, TrendingDown, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { fmtMoney } from "@/utils/money";

/**
 * Phase PAY-1 — Kadrlar → Əməkhaqqı (monthly summary).
 *
 * Lists every employee with an accrual/payment for the selected month.
 * Percentage-rule teachers show up automatically as soon as
 * attendance is marked; fixed-monthly rules appear once a payroll_run
 * finalises. Salary amounts are hidden with an `—` placeholder for
 * users whose role doesn't grant `hr.salary.view` / `payroll.view`.
 */
type Row = {
    employee_id: string;
    employee: { position?: string; user?: { email?: string; full_name?: string } | null } | null;
    accrued_minor: number | null;
    paid_minor: number | null;
    remaining_minor: number | null;
    sessions_count: number;
    status: "paid" | "partial" | "outstanding" | "none";
    currency: string;
    salary_masked?: boolean;
};

export default function PayrollSummary() {
    const { t } = useTranslation();
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const { data } = await api.get(`/payroll/summary?year=${year}&month=${month}`);
            setRows(data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [year, month]);
    useEffect(() => { load(); }, [load]);

    const totalAccrued = rows.reduce((s, r) => s + (r.accrued_minor || 0), 0);
    const totalPaid = rows.reduce((s, r) => s + (r.paid_minor || 0), 0);
    const totalRemaining = rows.reduce((s, r) => s + (r.remaining_minor || 0), 0);
    const anyMasked = rows.some(r => r.salary_masked);

    return (
        <div className="space-y-6" data-testid="payroll-summary-page">
            <div>
                <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">
                    {t("payroll.summary.title", { defaultValue: "Aylıq əməkhaqqı" })}
                </h1>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                    {t("payroll.summary.hint", { defaultValue: "Sessiya davamiyyəti təsdiqlənəndə faiz qaydası olan müəllimlərə avtomatik hesablanır." })}
                </p>
            </div>

            <div className="rounded-md p-3 text-xs" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="payroll-summary-explain">
                <div className="font-semibold mb-1">{t("payroll.summary.explain.title")}</div>
                <div>{t("payroll.summary.explain.body")}</div>
            </div>

            <div className="flex flex-wrap items-end gap-2" data-testid="payroll-summary-controls">
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("payroll.summary.month", { defaultValue: "Ay" })}</div>
                    <select data-testid="pr-month" value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
                        className="surface-input px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                            <option key={m} value={m}>{t(`month.${m}` as any, { defaultValue: String(m) })}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("payroll.summary.year", { defaultValue: "İl" })}</div>
                    <input data-testid="pr-year" type="number" min={2020} max={2100} value={year}
                        onChange={(e) => setYear(parseInt(e.target.value) || year)}
                        className="surface-input px-3 py-2 text-sm w-24" style={{ minHeight: 40 }} />
                </label>
            </div>

            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="payroll-summary-error">{err}</div>}

            {!anyMasked && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="payroll-summary-kpis">
                    <Kpi testid="kpi-accrued" label={t("payroll.summary.total_accrued", { defaultValue: "Cəmi hesablanan" })} value={fmtMoney(totalAccrued, "AZN")} icon={<Wallet size={16} />} />
                    <Kpi testid="kpi-paid" label={t("payroll.summary.total_paid", { defaultValue: "Ödənilmiş" })} value={fmtMoney(totalPaid, "AZN")} icon={<CheckCircle2 size={16} />} />
                    <Kpi testid="kpi-remaining" label={t("payroll.summary.total_remaining", { defaultValue: "Qalıq" })} value={fmtMoney(totalRemaining, "AZN")} icon={<TrendingDown size={16} />} />
                </div>
            )}

            <div className="surface-card overflow-hidden" data-testid="payroll-summary-table">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div> :
                    rows.length === 0 ? <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</div> : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                    <th className="px-4 py-2">{t("payroll.summary.col.employee", { defaultValue: "İşçi" })}</th>
                                    <th className="px-4 py-2 text-right">{t("payroll.summary.col.sessions", { defaultValue: "Sessiya" })}</th>
                                    <th className="px-4 py-2 text-right">{t("payroll.summary.col.accrued", { defaultValue: "Hesablanan" })}</th>
                                    <th className="px-4 py-2 text-right">{t("payroll.summary.col.paid", { defaultValue: "Ödənilmiş" })}</th>
                                    <th className="px-4 py-2 text-right">{t("payroll.summary.col.remaining", { defaultValue: "Qalıq" })}</th>
                                    <th className="px-4 py-2">{t("common.status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.employee_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`pr-row-${r.employee_id}`}>
                                        <td className="px-4 py-3">
                                            <Link to={`/hr/payroll/${r.employee_id}?year=${year}`} className="hover:underline"
                                                style={{ color: "var(--text)" }} data-testid={`pr-link-${r.employee_id}`}>
                                                {r.employee?.user?.full_name || r.employee?.user?.email || r.employee_id.slice(0, 8)}
                                            </Link>
                                            {r.employee?.position && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{r.employee.position}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>{r.sessions_count}</td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                                            {r.salary_masked ? <span title={t("payroll.masked", { defaultValue: "İcazə yoxdur" }) as string}>—</span> :
                                                fmtMoney(r.accrued_minor || 0, r.currency)}
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: "var(--text)" }}>
                                            {r.salary_masked ? "—" : fmtMoney(r.paid_minor || 0, r.currency)}
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums" style={{ color: (r.remaining_minor || 0) > 0 ? "var(--warning-fg)" : "var(--text)" }}>
                                            {r.salary_masked ? "—" : fmtMoney(r.remaining_minor || 0, r.currency)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`status-pill ${r.status === "paid" ? "status-success" : r.status === "partial" ? "status-warning" : r.status === "outstanding" ? "status-info" : "status-neutral"}`}>
                                                {t(`payroll.status.${r.status}` as any, { defaultValue: r.status })}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>

            {anyMasked && (
                <div className="rounded-md p-3 text-xs flex items-center gap-2" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="payroll-summary-masked">
                    <AlertCircle size={14} />
                    {t("payroll.masked_hint", { defaultValue: "Bəzi məbləğlər sizə görünmür — əməkhaqqı icazəniz məhduddur." })}
                </div>
            )}
        </div>
    );
}

function Kpi({ testid, label, value, icon }: { testid: string; label: string; value: string; icon: React.ReactNode }) {
    return (
        <div className="surface-card p-4" data-testid={testid}>
            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>{icon}{label}</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums" style={{ color: "var(--text)" }}>{value}</div>
        </div>
    );
}
